import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.tsx';
import { Eye, EyeOff, Lock, Mail, LogIn } from 'lucide-react';
import logo from '../assets/pawley-club-logo.png';
import Setup2FA from './Setup2FA';
import Login2FA from './Login2FA';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [show2FALogin, setShow2FALogin] = useState(false);
  const [forceNew2FA, setForceNew2FA] = useState(false); // 2FA sıfırlandıktan sonra yeni secret üretmek için
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        // Check if 2FA is already set up (backend'den kontrol et)
        const token = localStorage.getItem('adminToken');
        let has2FA = false;

        if (token) {
          try {
            const { API_BASE_URL, API_ENDPOINTS, getAuthHeaders } = await import('../config/api.ts');
            const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.STAFF_2FA_STATUS}`, {
              method: 'GET',
              headers: getAuthHeaders(token),
            });

            if (response.ok) {
              const data = await response.json();
              has2FA = data.success && data.enabled;
            }
          } catch (error) {
            // Backend hatası - fallback olarak localStorage kontrol et
            console.log('Backend 2FA endpoint bulunamadı, localStorage kullanılıyor');
          }
        }

        // Fallback: localStorage kontrol et
        if (!has2FA) {
          const hasSecret = localStorage.getItem(`twoFactorSecret_${email}`) !== null;
          has2FA = hasSecret;
        }
        
        if (has2FA) {
          // 2FA is set up for this user, require verification
          setShow2FALogin(true);
        } else {
          // First time login, require 2FA setup
          setShow2FASetup(true);
        }
      } else {
        // Show specific error message
        if (result.lockTime && result.lockTime > 0) {
          setError(`Hesabınız ${result.lockTime} dakika daha kilitli. Çok fazla başarısız giriş denemesi.`);
        } else if (result.remainingAttempts !== undefined) {
          setError(`${result.message} Kalan deneme hakkınız: ${result.remainingAttempts}`);
        } else {
          setError(result.message || 'E-posta veya şifre hatalı!');
        }
      }
    } catch (err) {
      setError('Giriş yapılırken bir hata oluştu!');
    }
    
    setIsLoading(false);
  };

  const handle2FASuccess = () => {
    navigate('/');
  };

  const handle2FASetupComplete = () => {
    localStorage.setItem('2FASetup', 'true');
    setShow2FASetup(false);
    navigate('/');
  };

  const handleClearTokens = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    setError('Token\'lar temizlendi! Sayfayı yenileyin.');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="w-full h-full bg-repeat" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
      </div>
      
      <div className="max-w-md w-full space-y-8 relative z-10">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 p-8">
          {/* Logo/Header */}
          <div className="text-center mb-8">
            <div className="mx-auto w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-6 shadow-lg">
              <img src={logo} alt="PawleyClub Logo" className="w-18 h-18 rounded-full object-cover" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">PawleyClub</h2>
            <p className="text-blue-100 text-lg font-medium">Admin Paneli</p>
            <p className="mt-2 text-sm text-blue-200">Admin hesabınızla giriş yapın</p>
          </div>

          {/* Login Form */}
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-500/20 border border-red-400/30 text-red-100 px-4 py-3 rounded-xl text-sm backdrop-blur-sm">
                {error}
                {error.includes('Token') && (
                  <button
                    onClick={handleClearTokens}
                    className="ml-2 underline hover:no-underline text-red-200"
                  >
                    Token'ları Temizle
                  </button>
                )}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                E-posta Adresi
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail size={18} className="text-blue-200" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-3 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40 transition-all duration-200"
                  placeholder="E-posta adresinizi girin"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white mb-2">
                Şifre
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock size={18} className="text-blue-200" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40 transition-all duration-200"
                  placeholder="Şifrenizi girin"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-blue-200 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center items-center py-3 px-4 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3"></div>
                ) : (
                  <LogIn size={18} className="mr-2" />
                )}
                {isLoading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
              </button>
            </div>

          </form>

        </div>
      </div>

      {/* 2FA Setup Modal */}
      {show2FASetup && (
        <Setup2FA
          onBack={() => {
            setShow2FASetup(false);
            setForceNew2FA(false); // Modal kapandığında flag'i sıfırla
          }}
          onComplete={() => {
            handle2FASetupComplete();
            setForceNew2FA(false); // Kurulum tamamlandığında flag'i sıfırla
          }}
          userEmail={email}
          forceNew={forceNew2FA} // 2FA sıfırlandıktan sonra true olur
        />
      )}

      {/* 2FA Login Modal */}
      {show2FALogin && (
        <Login2FA
          onBack={() => setShow2FALogin(false)}
          onSuccess={handle2FASuccess}
          onReset={() => {
            // 2FA sıfırlandığında Login2FA modalını kapat ve Setup2FA modalını aç
            setShow2FALogin(false);
            setForceNew2FA(true); // Yeni secret üretmeyi zorla
            setShow2FASetup(true);
          }}
          email={email}
        />
      )}
    </div>
  );
};

export default Login;
